import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAuditLog, getUsers } from '../api/client'
import { useStore } from '../store'
import type { AuditEntry, User } from '../types'

const ACTION_LABEL_KEYS: Record<string, { key: string; color: string }> = {
  LOGIN:            { key: 'audit_log.action.login',            color: 'bg-green-100 text-green-800' },
  LOGIN_FAILED:     { key: 'audit_log.action.login_failed',     color: 'bg-red-100 text-red-800' },
  LOGIN_MFA:        { key: 'audit_log.action.login_mfa',        color: 'bg-green-100 text-green-700' },
  LOGIN_MFA_FAILED: { key: 'audit_log.action.login_mfa_failed', color: 'bg-red-100 text-red-800' },
  PASSWORD_CHANGED: { key: 'audit_log.action.password_changed', color: 'bg-yellow-100 text-yellow-800' },
  UPLOAD:           { key: 'audit_log.action.upload',           color: 'bg-blue-100 text-blue-800' },
  SEND_EMAIL:       { key: 'audit_log.action.send_email',       color: 'bg-indigo-100 text-indigo-800' },
  DOWNLOAD_ZIP:     { key: 'audit_log.action.download_zip',     color: 'bg-gray-100 text-gray-700' },
  BULK_RENAME:      { key: 'audit_log.action.bulk_rename',      color: 'bg-purple-100 text-purple-800' },
  USER_CREATED:     { key: 'audit_log.action.user_created',     color: 'bg-teal-100 text-teal-800' },
  USER_UPDATED:     { key: 'audit_log.action.user_updated',     color: 'bg-amber-100 text-amber-800' },
  USER_DELETED:     { key: 'audit_log.action.user_deleted',     color: 'bg-red-100 text-red-700' },
  BCC_CHANGED:      { key: 'audit_log.action.bcc_changed',      color: 'bg-orange-100 text-orange-800' },
}

const ALL_ACTIONS = Object.keys(ACTION_LABEL_KEYS)

function fmt(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'medium' })
}

export function AuditLogViewer() {
  const { t } = useTranslation()
  const { setError } = useStore()
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filterUser, setFilterUser] = useState('')
  const [filterAction, setFilterAction] = useState('')
  const [offset, setOffset] = useState(0)
  const [users, setUsers] = useState<User[]>([])
  const LIMIT = 100

  useEffect(() => {
    getUsers().then(setUsers).catch(() => {/* silent fail */})
  }, [])

  const load = async (off = 0) => {
    setLoading(true)
    try {
      const res = await getAuditLog({ limit: LIMIT, offset: off, username: filterUser || undefined, action: filterAction || undefined })
      setEntries(res.entries)
      setTotal(res.total)
      setOffset(off)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('audit_log.error_load'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(0) }, [filterUser, filterAction])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('audit_log.filter_user')}</label>
          <select
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 w-44 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">{t('audit_log.all_users')}</option>
            {users.map(u => (
              <option key={u.id} value={u.username}>{u.username}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('audit_log.filter_action')}</label>
          <select
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">{t('audit_log.all_actions')}</option>
            {ALL_ACTIONS.map(a => (
              <option key={a} value={a}>{t(ACTION_LABEL_KEYS[a].key)}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-gray-400 self-end pb-2">
          {loading ? t('common.loading') : t('audit_log.row_count', { count: total, plural: total === 1 ? t('audit_log.row_singular') : t('audit_log.row_plural') })}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[700px]">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">{t('audit_log.col_timestamp')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">{t('audit_log.col_user')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">{t('audit_log.col_action')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('audit_log.col_details')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">{t('audit_log.col_ip')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {entries.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-400">{t('audit_log.no_entries')}</td>
                </tr>
              )}
              {entries.map(e => {
                const meta = ACTION_LABEL_KEYS[e.action]
                return (
                  <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">{fmt(e.timestamp)}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">{e.username}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${meta?.color ?? 'bg-gray-100 text-gray-600'}`}>
                        {meta ? t(meta.key) : e.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono">{e.details ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-400 dark:text-gray-500 font-mono whitespace-nowrap">{e.ip_address ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {total > LIMIT && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between text-xs text-gray-500">
            <span>{offset + 1}–{Math.min(offset + LIMIT, total)} {t('audit_log.of')} {total}</span>
            <div className="flex gap-2">
              <button
                onClick={() => load(Math.max(0, offset - LIMIT))}
                disabled={offset === 0}
                className="px-3 py-1 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
              >
                {t('audit_log.prev')}
              </button>
              <button
                onClick={() => load(offset + LIMIT)}
                disabled={offset + LIMIT >= total}
                className="px-3 py-1 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
              >
                {t('audit_log.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
